import { useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { Button, Icon } from "@/shared/ui";
import { ServiceCardEnum } from "@/domains/business/constants/serviceCardEnum";
import BusinessCardApply from "@/domains/business/components/BusinessCardApply";
import BusinessServiceCircle from "@/domains/business/components/service/BusinessServiceCircle";

type Props = {
  type: ServiceCardEnum;
  progress: number;
  effect: number;
  price: number;
  onClickFirstButton: () => void;
  onClickSecondButton: () => void;
  isHandleOnly?: boolean;
  circleSize?: number;
  circleStrokeWidth?: number;
  circleBaseColor?: string;
  circleGradientFrom?: string;
  circleGradientTo?: string;
  textColor?: string;
  effectColor?: string;
};

export default function BusinessServiceCard({
  type,
  progress,
  effect,
  price,
  onClickFirstButton,
  onClickSecondButton,
  isHandleOnly,
  circleSize,
  circleStrokeWidth,
  circleBaseColor,
  circleGradientFrom,
  circleGradientTo,
  textColor = "#30A8FF",
  effectColor = "radial-gradient(50% 122.83% at 50% 100%, rgba(0, 127, 219, 0.14) 0%, rgba(0, 127, 219, 0.00) 100%), linear-gradient(90deg, rgba(0, 127, 219, 0.08) 0%, rgba(1, 100, 172, 0.08) 100%)",
}: Props) {
  const { t } = useTranslation("business");
  const [isApplyScreen, setIsApplyScreen] = useState(false);

  const onFirstButton = () => {
    setIsApplyScreen(true);
    onClickFirstButton();
  };

  return (
    <div className="relative flex flex-col items-center pt-7.5 px-5 pb-5 bg-dark-800 rounded-[0.75rem] overflow-hidden">
      <p className="mb-0.5 text-20 font-bold">{t(`business.service.${type}.title`)}</p>
      <p className="mb-6 text-16 font-medium">{t(`business.service.${type}.currentStatus`)}</p>
      <BusinessServiceCircle
        progress={progress}
        size={circleSize}
        strokeWidth={circleStrokeWidth}
        baseColor={circleBaseColor}
        gradientFrom={circleGradientFrom}
        gradientTo={circleGradientTo}
      />
      <p className="max-w-[18.3125rem] mt-5 mb-4 text-14 text-center text-white/40">
        {t(`business.service.${type}.statusDescription`)}
      </p>
      <div
        className="w-full h-[4.625rem] flex flex-col justify-center mb-2 text-center rounded-2xl"
        style={{ background: effectColor }}
      >
        <p className="text-20 font-bold">{effect}%</p>
        <p className="text-15 font-medium" style={{ color: textColor }}>
          {t("business.service.impactOnIncome")}
        </p>
      </div>
      {!isHandleOnly && (
        <Button className="justify-between w-full h-11 mb-2" onClick={onFirstButton}>
          {t(`business.service.${type}.callUp`)}{" "}
          <span className="ml-1 text-14">{formatPrice(price)}₽</span>
        </Button>
      )}
      <Button className="justify-between w-full h-11" onClick={onClickSecondButton}>
        {t(`business.service.${type}.make`)}
        <Icon category="business" name={type} className="size-4 ml-2" />
      </Button>
      <BusinessCardApply
        isShow={isApplyScreen}
        setIsShow={setIsApplyScreen}
        title={t(`business.service.${type}.isCalled`)}
        subTitle={t(`business.service.${type}.result`)}
        timer={5}
      />
    </div>
  );
}
